import { useEffect, useRef, useState } from 'react'
import {
    ApartmentOutlined,
    CloseOutlined,
    DownOutlined,
    RightOutlined,
    ExportOutlined,
    ReloadOutlined,
} from '@ant-design/icons'

import imgEmpty from '@img/empty.svg'
import { Button, Empty, Loader, ToolTip } from '@ui/index'

import { buildListenerJourneyRegistry } from './graph'
import { IListenerJourneyView, ListenerJourneyRecord } from './types'
import css from './ui.module.scss'

export const ListenerJourney = ({
    isOpen,
    data,
    isLoading,
    errorMessage,
    onClose,
    onRetry,
    onNodeAction,
}: IListenerJourneyView) => {
    const viewportRef = useRef<HTMLDivElement>(null)
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const listenerID = data?.listener.listenerID
    useEffect(() => {
        setExpanded(new Set())
        if (viewportRef.current) viewportRef.current.scrollTop = 0
    }, [isOpen, listenerID])

    useEffect(() => {
        if (!isOpen) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    if (!isOpen) return null

    const fullName = data
        ? [data.listener.lastName, data.listener.firstName, data.listener.secondName].filter(Boolean).join(' ')
        : 'Полный цикл слушателя'
    const records = data ? buildListenerJourneyRegistry(data) : []
    const toggle = (id: string) => setExpanded(previous => {
        const next = new Set(previous)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
    })
    const renderRecord = (record: ListenerJourneyRecord, depth = 0) => <div className={css.record} key={record.id} data-depth={Math.min(depth, 3)}>
        <div className={css.recordRow}>
            <button type="button" className={css.disclosure} aria-expanded={expanded.has(record.id)} aria-controls={`journey-${record.id}`} onClick={() => toggle(record.id)}>
                {expanded.has(record.id) ? <DownOutlined aria-hidden="true" /> : <RightOutlined aria-hidden="true" />}<span>{record.title}</span>
            </button>
            <span className={css.status} data-state={record.state || 'neutral'}>{record.status}</span>
            <span className={css.recordSummary}>{record.summary}</span>
            <span className={css.recordAction}>{record.action && <ToolTip title={record.action.type === 'scan' ? 'Открыть документ' : 'Открыть анкету'}>
                <Button variant="icon" size="sm" onClick={() => onNodeAction(record.action!)}><ExportOutlined aria-hidden="true" /><span className={css.visuallyHidden}>Открыть: {record.title}</span></Button>
            </ToolTip>}</span>
        </div>
        {expanded.has(record.id) && <div className={css.details} id={`journey-${record.id}`}>
            <table aria-label={`Реквизиты: ${record.title}`}><tbody>
                <tr><th scope="row">Статус</th><td>{record.status}</td></tr>
                {record.fields.map(([label, value]) => <tr key={label}><th scope="row">{label}</th><td>{String(value ?? '').trim() || 'Нет данных'}</td></tr>)}
            </tbody></table>
        </div>}
        {record.children?.map(child => renderRecord(child, depth + 1))}
    </div>

    return <section className={css.overlay} role='dialog' aria-modal='true' aria-label={`Полный цикл: ${fullName}`}>
        <header className={css.header}>
            <div className={css.heading}>
                <span className={css.headingIcon} aria-hidden='true'><ApartmentOutlined /></span>
                <div className={css.headingText}>
                    <h2>{fullName}</h2>
                    <p>{data ? `Полный цикл · Анкета №${data.listener.listenerID}` : 'Полный цикл слушателя'}</p>
                </div>
            </div>

            <div className={css.overview}>{data && <><span>Заявок: {data.applications.length}</span><span>Договоров: {new Set(data.contracts.map(item => item.contractID)).size}</span></>}</div>

            <div className={css.toolbar}>
                <ToolTip title='Обновить данные' placement='bottom'>
                    <Button variant='icon' size='sm' isDisabled={isLoading} onClick={onRetry}>
                        <ReloadOutlined aria-hidden='true' />
                        <span className={css.visuallyHidden}>Обновить данные</span>
                    </Button>
                </ToolTip>
                <ToolTip title='Закрыть' placement='bottomRight'>
                    <Button variant='icon' size='sm' onClick={onClose}>
                        <CloseOutlined aria-hidden='true' />
                        <span className={css.visuallyHidden}>Закрыть</span>
                    </Button>
                </ToolTip>
            </div>
        </header>

        <div className={css.workspace}>
            {isLoading && <Loader isOpen backdrop title='Загрузка полного цикла...' />}

            {!isLoading && errorMessage && <div className={css.statePanel} role='alert'>
                <Empty image={imgEmpty} size='132px' title={errorMessage} />
                <Button size='sm' onClick={onRetry}><ReloadOutlined aria-hidden='true' />Повторить</Button>
            </div>}

            {!errorMessage && data && <div
                ref={viewportRef}
                className={css.viewport}
                aria-label='Реестр полного цикла слушателя'
            >
                <div className={css.registry}>
                    <div className={css.listenerSummary}>
                        <span>{data.listener.phoneNumber || 'Телефон не указан'}</span>
                        <span>{data.listener.snils ? `СНИЛС ${data.listener.snils}` : 'СНИЛС не указан'}</span>
                        <Button variant="primaryDark" size="sm" onClick={() => onNodeAction({ type: 'listener', cardID: data.listener.listenerID })}>Анкета слушателя</Button>
                    </div>
                    <div className={css.columnHeaders} aria-hidden="true"><span>Запись</span><span>Статус</span><span>Ключевые сведения</span><span /></div>
                    {records.map(record => renderRecord(record))}
                    {!records.length && <Empty image={imgEmpty} size="132px" title="Связанных записей пока нет" />}
                </div>
            </div>}
        </div>
    </section>
}